/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import FeaturesService from 'ember-feature-flags/services/features';
import Component from '@glimmer/component';
import { dropTask, task } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import { htmlSafe } from '@ember/template';
import DS from 'ember-data';
import cloneDeep from 'lodash.clonedeep';
import ApiErrorsService from 'cio/services/api-errors';
import ImportModel, { IMPORT_TYPES } from 'cio/models/import';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';
import { joinWordsAsSnippets } from 'cio/utils/join-words';
import AuthService from 'cio/services/auth';
import ObjectTypesService from 'cio/services/object-types';
import { ImportSource } from 'cio/models/import';

enum ImportType {
  people = 'people',
  events = 'events',
}

interface SetupArgs {
  import: ImportModel;
  type: ImportType;
  segmentID: string;
  previousRouteName: 'people' | 'imports' | 'segments.segment' | 'objects.type';
  onFileChange?: () => unknown;
  objectTypeId?: string;
}

export const OPTION_EVERYTHING = 'everything';
export const OPTION_EXISTING = 'existing';
export const OPTION_NEW = 'new';
export const OPTION_MATCH_EMAIL = 'match_email';

export default class Setup extends Component<SetupArgs> {
  @service auth!: AuthService;
  @service features!: FeaturesService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service('objectTypes') objectTypesService!: ObjectTypesService;

  readonly identifierKey = IdentifierKey;
  readonly acceptedFileExtensions = ['csv'];

  get importOptions() {
    let objectTypes = this.store.peekAll('object-type');

    let importOptions = [
      {
        name: 'people_import',
        id: 'option_people_import',
        disabled: this.disabled,
        checked: this.args.import.isPeopleImport,
        label: 'People',
        type: IMPORT_TYPES.people,
        exampleUrl: this.importPeopleExampleUrl,
        description: `An ${this.requiredIdentifiers} is required to create new people. You can also update people with <code>cio_id</code>.`,
      },
      {
        name: 'event_import',
        id: 'option_event_import',
        disabled: this.disabled,
        checked: this.args.import.isEventsImport,
        label: 'Events',
        type: IMPORT_TYPES.event,
        exampleUrl: this.importEventsExampleUrl,
        description: `Event name and ${this.requiredIdentifiers} are required to import events.`,
      },
      ...objectTypes.toArray().map((objectType) => ({
        objectTypeId: objectType.id,
        name: `${objectType.slug}_import`,
        id: `option_${objectType.slug}_import`,
        disabled: this.disabled,
        checked: this.args.import.isObjectsImport && this.args.import.objectTypeId === objectType.id,
        label: objectType.name,
        type: IMPORT_TYPES.object,
        exampleUrl: this.importObjectsExampleUrl,
        description: `An <code>object_id</code> is required to create new ${objectType.name}.`,
      })),
    ];

    if (this.objectTypesService.hasObjectTypes) {
      importOptions.push({
        name: 'relationship_import',
        id: 'option_relationships_import',
        disabled: this.disabled,
        checked: this.args.import.isRelationshipsImport,
        label: 'Relationships',
        type: IMPORT_TYPES.relationship,
        exampleUrl: this.importRelationshipsExampleUrl,
        description: `An <code>object_type_id</code>, <code>object_id</code> and either <code>id</code>, <code>email</code> or <code>cio_id</code> is required to create relationships.`,
      });
    }

    return importOptions;
  }

  get cioIdAvailable() {
    return (
      this.environment.current.isMultiIdentifier &&
      this.environment.current.immutableIdentifiers.length !== this.environment.current.enabledIdentifiers.length
    );
  }

  get requiredIdentifiers() {
    return joinWordsAsSnippets(this.environment.current.enabledIdentifiers, ' ', ' or ');
  }

  get identifierOptions() {
    let base = [
      { label: IdentifierKey.Id, value: IdentifierKey.Id },
      { label: IdentifierKey.Email, value: IdentifierKey.Email },
    ];

    if (this.cioIdAvailable && !this.args.import.isEventsImport) {
      return [...base, { label: IdentifierKey.CioId, value: IdentifierKey.CioId }];
    }

    return base;
  }

  get errorData() {
    let { error } = this.args.import;
    let title = 'There was an issue processing your file:';

    if (!error) {
      return;
    }

    try {
      let { message } = JSON.parse(error);

      if (message.includes('"_cio_name" and "_cio_customer_id" are mandatory')) {
        title = 'Required attributes event name and id or email are missing in your file';
        message = htmlSafe(
          `Make sure to add the “_cio_” prefix to column names as per the <a
            href=${this.importEventsExampleUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
          example file
          </a>`,
        );
      }

      return {
        title,
        message,
      };
    } catch {
      return {
        title,
        message: error,
      };
    }
  }

  get onlyExistingError() {
    let { validateRules } = this.args.import;
    if (validateRules.identifier === IdentifierKey.CioId) {
      return htmlSafe(
        `Not available when you identify people by Customer.io-generated <code>${IdentifierKey.CioId}</code>`,
      );
    }

    if (validateRules.excludeExisting) {
      return `You must either add new ${this.getSelectedType} or update existing ${this.getSelectedType}`;
    }

    return;
  }

  get identifierDisabledMessage() {
    let addingNew = !this.args.import.validateRules.onlyExisting;

    if (addingNew && !this.environment.current.canIdentifyByEmail) {
      return `If you are adding new people, you can only identify them by ${IdentifierKey.Id}`;
    } else if (addingNew && !this.environment.current.canIdentifyById) {
      return `If you are adding new people, you can only identify them by ${IdentifierKey.Email}`;
    }

    return;
  }

  get importPeopleExampleUrl() {
    return 'https://docs.google.com/spreadsheets/d/1zVgwD_9nYC4jXk9LMUaJJFXlTtKIEQQBbO6Gt489sBo/edit?usp=sharing';
  }

  get importEventsExampleUrl() {
    return 'https://docs.google.com/spreadsheets/d/1LTENBpzZkB926vwNc4z2WRct7H53CLGHavBf2pQEqiI/edit?usp=sharing';
  }

  get importObjectsExampleUrl() {
    return 'https://docs.google.com/spreadsheets/d/1YKzpk07N1J9Vqw6xVwYqQARC0ut7HswJpzTmhbSh7L0/edit#gid=1720036590';
  }

  get importRelationshipsExampleUrl() {
    return 'https://docs.google.com/spreadsheets/d/1YKzpk07N1J9Vqw6xVwYqQARC0ut7HswJpzTmhbSh7L0/edit#gid=23587959';
  }

  get tooltipSummary() {
    let condition = false;
    let content;

    if (this.args.import.isExpired) {
      condition = true;

      if (this.args.import.isExpired) {
        content = 'The imported CSV is no longer available. Re-upload your CSV to continue.';
      }
    }

    if (!this.args.import.type) {
      condition = true;
      content = 'Choose type of import to continue.';
    }

    return { condition, content };
  }

  get importNameErrors() {
    if (this.args.import.name?.length > 190) {
      return ['Maximum limit of 190 characters exceeded'];
    }

    return;
  }

  get disabled() {
    if (!this.args.import.isNew) {
      return !this.auth.hasPermission('journeys/imports/edit');
    }

    return !this.auth.hasPermission('journeys/imports/create');
  }

  get getSelectedType() {
    if (this.args.import.isRelationshipsImport) {
      return 'relationships';
    } else if (this.args.import.objectTypeId != null) {
      return this.args.import.objectType?.name.toLowerCase();
    } else {
      return 'people';
    }
  }

  get isSourceExtApi() {
    return this.args.import.source === ImportSource.API;
  }

  startPreprocessing = dropTask(async () => {
    try {
      let record = this.args.import;
      // Make the initial POST request which is limited in scope of what can be set,
      // before preprocessing the import
      // @ts-ignore - isNew is a computed property, which looks like a function to TS
      if (record.isNew) {
        // The backend only saves validateRules and importRules on certain
        // updateTypes. Here, after saving, "validateRules" and "importRules"
        // will come back as null, so any changes to settings will be lost.
        // We need to save the current rules and re-apply them, so that they're
        // present for the "preprocess" call below.
        let validateRules = cloneDeep(record.validateRules);
        let importRules = cloneDeep(record.importRules);
        await record.save();
        record.validateRules = validateRules;
        record.importRules = importRules;
      }

      await record.preprocess.perform();
      this.router.transitionTo('imports.import.setup.preprocessing', this.args.import.id, {
        queryParams: {
          previous_route: this.args.previousRouteName,
        },
      });
    } catch (errors) {
      this.apiErrors.report(errors);
    }
  });

  resetImport = task(async () => {
    try {
      let validateRules = {
        ...this.args.import.validateRules,
        mapAttributes: [],
        skipAttributes: [],
        ignoreRestrictedAttributes: [],
      };
      let importRules = { ...this.args.import.importRules };
      await this.args.import.resetState.perform();
      await this.args.import.updateImportRules.perform(importRules);
      return this.args.import.updateValidateRules.perform(validateRules);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  @action
  didUploadCSV({ key, filename }: Record<string, any>) {
    this.args.import.setProperties({
      filename,
      name: filename,
      path: key,
      sheet: null,
      error: null,
    });

    this.onFileChange();

    if (!this.args.import.isNew) {
      this.resetImport.perform();
    }
  }

  @action
  didImportGoogleSheet({ sheetId, sheetName, spreadsheetId, spreadsheetName }: Record<string, any>) {
    this.args.import.setProperties({
      error: null,
      sheet: { sheetId, spreadsheetId, sheetName, spreadsheetName },
      name: `${sheetName} from Google Sheet ${spreadsheetName}`,
    });

    this.onFileChange();

    if (!this.args.import.isNew) {
      this.resetImport.perform();
    }
  }

  @action
  didDeleteUpload() {
    this.analytics.track('import', 'upload_deleted', { model: this.args.import });

    this.args.import.setProperties({
      filename: null,
      name: null,
      path: null,
      sheet: null,
      error: null,
    });

    if (!this.args.import.isNew) {
      this.resetImport.perform();
    }
  }

  @action
  didSelectClearEmptyCellsValue(value: string) {
    let clear = value === 'true';

    this.args.import.importRules = { ...this.args.import.importRules, clearEmptyCells: clear };
  }

  @action
  async onToggleImportType(option: (typeof this.importOptions)[number] & { objectTypeId?: string }) {
    this.args.import.setProperties({
      type: option.type,
      objectTypeId: option.objectTypeId ?? null,
    });

    let validateRules = {
      ...this.args.import.validateRules,
    };

    if (option.objectTypeId != null) {
      validateRules.identifier = ObjectIdentifierKey.ObjectId;
    } else {
      validateRules.identifier = this.environment.current.canIdentifyByEmail ? IdentifierKey.Email : IdentifierKey.Id;
    }

    this.args.import.validateRules = validateRules;

    if (!this.args.import.isNew) {
      await this.resetImport.perform();
    }
  }

  @action
  toggleAddNewPeople() {
    this.args.import.validateRules = {
      ...this.args.import.validateRules,
      onlyExisting: !this.args.import.validateRules.onlyExisting,
    };

    if (!this.args.import.validateRules.onlyExisting && !this.environment.current.canIdentifyByEmail) {
      this.selectIdentifierOption(IdentifierKey.Id);
    }
  }

  @action
  toggleUpdateExistingPeople() {
    this.args.import.validateRules = {
      ...this.args.import.validateRules,
      excludeExisting: !this.args.import.validateRules.excludeExisting,
    };
  }

  @action
  selectIdentifierOption(value: IdentifierKey) {
    let rules = { ...this.args.import.validateRules };

    rules.matchEmail = value === IdentifierKey.Email;

    if (this.args.import.validateRules.identifier !== value) {
      rules.identifier = value;
    }

    // cio id can't be used to import new people, so force update only
    if (value === IdentifierKey.CioId) {
      rules.onlyExisting = true;
    }

    this.args.import.validateRules = rules;
  }

  @action
  exampleFileClicked() {
    this.analytics.track('import', 'example_link_clicked', { model: this.args.import });
  }

  @action
  didReplaceFile() {
    this.analytics.track('import', 'replace_clicked', { model: this.args.import });
  }

  @action
  onFileChange() {
    this.args.onFileChange?.();
  }

  @action
  async deleteImport() {
    this.analytics.track('import', 'canceled', { model: this.args.import, source: 'imports.new' });
    try {
      await this.args.import.destroyRecord();
      this.router.transitionTo(
        this.args.previousRouteName ?? 'imports',
        this.args.segmentID ?? this.args.objectTypeId ?? this.environment.current.id,
      );
    } catch (e) {
      this.apiErrors.report(e);
      this.args.import.rollbackAttributes();
    }
  }
}
